import {
  Outlet,
  ShieldCheckIcon,
  ThemeSwitcher,
  useAuthStore,
  useCharity,
} from "@";

const NotAuthLayout = () => {
  const { user } = useAuthStore();
  const { charityLogo, charityName, isLoading } = useCharity();

  if (user) {
    window.location.replace("/statistics");
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#180a26] lg:bg-white  dark:lg:bg-gray-900">
      {/* Theme switcher — fixed to the top corner so it's reachable on auth screens */}
      <ThemeSwitcher className="absolute start-6 top-6 z-30" />

      <div className="relative grid min-h-screen lg:grid-cols-2">
        {/* ─── FORM PANEL — visually right (start) in RTL ─── */}
        <section className="relative flex min-h-screen flex-col ">
          {/* Mobile-only ambient lights + dot grid (matching brand panel) */}
          <span
            aria-hidden
            className="pointer-events-none absolute -end-32 -top-32 h-[440px] w-[440px] rounded-full bg-gradient-to-br from-purple-500/25 via-purple-700/10 to-transparent blur-3xl lg:hidden"
          />
          <span
            aria-hidden
            className="pointer-events-none absolute -bottom-32 -start-32 h-[440px] w-[440px] rounded-full bg-gradient-to-br from-fuchsia-500/15 via-purple-800/10 to-transparent blur-3xl lg:hidden"
          />
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-[0.07] [background-image:radial-gradient(circle_at_1px_1px,_white_1px,_transparent_0)] [background-size:24px_24px] lg:hidden"
          />

          {/* Mobile header: logo (matches brand panel style) */}
          <header className="relative z-10 flex items-center justify-center px-6 py-6 sm:px-10 lg:hidden">
            <a href="/" className="group inline-flex items-center gap-3">
              <span className="hidden text-sm font-bold tracking-tight text-white sm:inline-block">
                {!isLoading && charityName}
              </span>
              {isLoading ? (
                <span className="block h-16 w-16 animate-pulse rounded-2xl bg-white/10" />
              ) : (
                <span className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-2xl bg-white p-1.5 shadow-md shadow-purple-900/30 ring-1 ring-white/10">
                  <img
                    src={charityLogo}
                    alt={charityName}
                    className="h-full w-full rounded-2xl object-contain"
                  />
                </span>
              )}
            </a>
          </header>

          {/* Centered form — white card on mobile, naked on lg+ */}
          <div className="relative flex flex-1 items-center justify-center px-4 py-6 sm:px-6 lg:px-12 lg:py-12">
            <div className="w-full max-w-md">
              <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-white dark:bg-gray-900/20 dark:border-gray-900/20  p-6 shadow-2xl shadow-black/40 backdrop-blur-md sm:p-8 lg:rounded-none lg:border-0 lg:bg-transparent lg:p-0 lg:shadow-none lg:backdrop-blur-0 dark:lg:bg-transparent">
                {/* subtle top accent stripe — mobile only */}
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-transparent via-purple-500/60 to-transparent lg:hidden"
                />
                <Outlet />
              </div>

              {/* Mobile copyright — under the card */}
              <p className="mt-6 text-center text-[11px] text-purple-200/60 lg:hidden">
                جميع الحقوق محفوظة © {new Date().getFullYear()}
              </p>
            </div>
          </div>
        </section>

        {/* ─── BRAND PANEL — visually left (end) in RTL, lg+ only ─── */}
        <aside className="relative hidden overflow-hidden bg-[#180a26]  text-white lg:flex lg:flex-col lg:justify-between lg:p-10 xl:p-14">
          {/* Ambient lights */}
          <span
            aria-hidden
            className="pointer-events-none absolute -end-40 -top-40 h-[640px] w-[640px] rounded-full bg-gradient-to-br from-purple-500/25 via-purple-700/10 to-transparent blur-3xl dark:from-purple-700/25 dark:via-purple-900/10 dark:to-transparent"
          />
          <span
            aria-hidden
            className="pointer-events-none absolute -bottom-40 -start-40 h-[600px] w-[600px] rounded-full bg-gradient-to-br from-fuchsia-500/15 via-purple-800/10 to-transparent blur-3xl dark:from-purple-800/15 dark:via-purple-950/10 dark:to-transparent"
          />
          {/* Dot grid */}
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-[0.07] [background-image:radial-gradient(circle_at_1px_1px,_white_1px,_transparent_0)] [background-size:24px_24px] dark:opacity-[0.04]"
          />
          {/* Faint horizon line */}
          <span
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-[42%] h-px bg-gradient-to-r from-transparent via-white/10 to-transparent dark:via-gray-800/10"
          />

          {/* TOP — brand */}
          <div className="relative">
            <a href="/" className="inline-flex items-center gap-3">
              <span className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-2xl bg-white p-1.5 shadow-md shadow-purple-900/30 ring-1 ring-white/10">
                {!isLoading && (
                  <img
                    src={charityLogo}
                    alt={charityName}
                    className="h-full w-full rounded-2xl object-contain"
                  />
                )}
              </span>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-purple-300">
                  لوحة التحكم
                </p>
                <h1 className="text-base font-bold tracking-tight text-white">
                  {isLoading ? (
                    <span className="block h-5 w-32 animate-pulse rounded bg-white/10" />
                  ) : (
                    charityName
                  )}
                </h1>
              </div>
            </a>
          </div>

          {/* MIDDLE — tagline + pills + differentiator */}
          <div className="relative mt-12 max-w-xl">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-purple-300 ">
              منصة سعودية للجمعيات الخيرية
            </p>
            <h2 className="mt-3 text-3xl font-black leading-[1.15] tracking-tight text-white dark:text-gray-300 xl:text-[44px]">
              خزامى{" "}
              <span className="bg-gradient-to-l from-purple-200 via-fuchsia-300 to-purple-200 bg-clip-text text-transparent">
                طريقك للاستدامة
              </span>
            </h2>
            <p className="mt-4 text-base leading-relaxed text-purple-100/70 dark:text-purple-100">
              منصّة تُمكّن الجمعيات الخيرية من إطلاق متاجر تبرعات ذكية — بأدوات
              مبتكرة لتجربة عطاء موثوقة، سهلة، ومستدامة.
            </p>

            {/* Feature pills */}
            <ul className="mt-6 flex flex-wrap gap-2">
              {[
                "إدارة المتبرعين",
                "تقارير وإحصائيات",
                "توثيق الطلبات",
                "تبرع دوري تلقائي",
                "تعدد بوابات الدفع",
                "رسائل SMS",
              ].map((label) => (
                <li
                  key={label}
                  className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs font-medium text-purple-100 backdrop-blur-sm"
                >
                  <span className="h-1 w-1 rounded-full bg-purple-300" />
                  {label}
                </li>
              ))}
            </ul>
 
          </div>

          {/* BOTTOM — security + support */}
          <div className="relative mt-10 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-purple-200/60">
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheckIcon className="h-4 w-4 text-purple-300" />
              اتصال آمن ومشفّر
            </span>
            <span aria-hidden className="text-white/20">
              ·
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
              </span>
              دعم فني مباشر
            </span>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default NotAuthLayout;
